// web-frontend/src/components/chat/ChatbotMiniMap.jsx
// 🔥 v5.0 - 입구/출구 표시 추가 (왼쪽 입구, 가운데 입구, 오른쪽 출구)
// 🔧 3D 맵과 동일한 경로를 2D 미니맵에 표시
import React, { useMemo } from 'react';
import { Navigation, ZoomIn } from 'lucide-react';
import logger from '../../shared/utils/logger';
import { getVenueName } from '../../shared/config/tenant';
import './ChatbotMiniMap.css';
import boothData from '../../assets/map/booth_mobile.json';

/**
 * ChatbotMiniMap - 챗봇 내 미니맵 컴포넌트
 * 
 * @param {string} fromBooth - 출발 부스 번호 (예: "B01")
 * @param {string} toBooth - 도착 부스 번호 (예: "G12")
 * @param {Array} pathPoints - 🔥 백엔드에서 받은 경로 좌표 배열 [{x, y}, ...]
 * @param {Function} onClick - 클릭 시 3D 맵으로 이동
 */
const ChatbotMiniMap = ({ fromBooth, toBooth, pathPoints, allFacilities, onClick }) => {
  
  // ==================== 스케일 상수 ====================
  // SVG viewBox: 300x180, booth_mobile.json: w x h
  const SCALE_X = 300 / boothData.w;
  const SCALE_Y = 180 / boothData.h;
  
  // ==================== 부스 좌표 찾기 ====================
  const getBoothPosition = (boothNumber) => {
    if (!boothNumber) return null;
    
    const boothId = boothNumber.toUpperCase().replace('-', '');
    const booth = boothData.booths.find(b => b.i === boothId);
    
    if (!booth) {
      logger.warn(`⚠️ 부스 ${boothId}를 찾을 수 없음`);
      return null;
    }
    
    // 부스 중심 좌표 계산 (스케일링 적용)
    const centerX = (booth.x + booth.w / 2) * SCALE_X;
    const centerY = (booth.y + booth.h / 2) * SCALE_Y;
    
    return { x: centerX, y: centerY, booth };
  };
  
  const fromPos = useMemo(() => getBoothPosition(fromBooth), [fromBooth]);
  const toPos = useMemo(() => getBoothPosition(toBooth), [toBooth]);
  
  // 🆕 편의시설 데이터 메모이제이션
  const facilities = useMemo(() => {
    return allFacilities || [];
  }, [allFacilities]);
  
  // ==================== 🔥 경로 데이터 변환 ====================
  const scaledPathPoints = useMemo(() => {
    // pathPoints가 있으면 스케일링하여 사용
    if (pathPoints && Array.isArray(pathPoints) && pathPoints.length >= 2) {
      logger.debug('📍 백엔드 경로 사용:', pathPoints.length, '포인트');
      
      return pathPoints.map(point => ({
        x: point.x * SCALE_X,
        y: point.y * SCALE_Y
      }));
    }
    
    // pathPoints가 없으면 출발/도착 좌표로 직선 경로 생성 (fallback)
    if (fromPos && toPos) {
      logger.debug('⚠️ 경로 데이터 없음, 직선 경로 사용');
      return [
        { x: fromPos.x, y: fromPos.y },
        { x: toPos.x, y: toPos.y }
      ];
    }
    
    return [];
  }, [pathPoints, fromPos, toPos, SCALE_X, SCALE_Y]);
  
  // ==================== 🔥 SVG Path 생성 ====================
  const pathD = useMemo(() => {
    if (scaledPathPoints.length < 2) return '';
    
    // 포인트들을 연결하는 polyline 스타일 path
    const points = scaledPathPoints;
    
    // 시작점
    let d = `M ${points[0].x} ${points[0].y}`;
    
    // 중간 포인트들 - 부드러운 곡선으로 연결
    if (points.length === 2) {
      // 2개 포인트: 직선
      d += ` L ${points[1].x} ${points[1].y}`;
    } else if (points.length === 3) {
      // 3개 포인트: 중간점을 통과하는 곡선
      d += ` Q ${points[1].x} ${points[1].y}, ${points[2].x} ${points[2].y}`;
    } else {
      // 4개 이상: 각 포인트를 순차적으로 연결 (약간의 곡선 처리)
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        
        // 직선 연결 (복도 기반 경로는 주로 직각 이동)
        d += ` L ${curr.x} ${curr.y}`;
      }
    }
    
    return d;
  }, [scaledPathPoints]);
  
  // ==================== 🔥 경로 정보 텍스트 ====================
  const pathInfo = useMemo(() => {
    if (scaledPathPoints.length < 2) return null;
    
    // 총 거리 계산 (픽셀 단위 → 대략적인 미터 환산)
    let totalDistance = 0;
    for (let i = 1; i < scaledPathPoints.length; i++) {
      const dx = scaledPathPoints[i].x - scaledPathPoints[i-1].x;
      const dy = scaledPathPoints[i].y - scaledPathPoints[i-1].y;
      totalDistance += Math.sqrt(dx * dx + dy * dy);
    }
    
    // 스케일 역산 후 미터 환산 (대략 1픽셀 = 0.5m 가정)
    const meters = Math.round(totalDistance / SCALE_X * 0.5);
    const minutes = Math.ceil(meters / 50); // 50m/분 도보 속도
    
    return { meters, minutes, waypoints: scaledPathPoints.length };
  }, [scaledPathPoints, SCALE_X]);
  
  return (
    <div 
      className="chatbot-minimap-container"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* 헤더 */}
      <div className="minimap-header">
        <Navigation size={14} />
        <span>경로 미리보기</span>
        <div className="minimap-zoom-hint">
          <ZoomIn size={12} />
          <span>클릭하여 상세 보기</span>
        </div>
      </div>
      
      {/* 미니맵 SVG */}
      <svg 
        viewBox="0 0 300 180" 
        className="minimap-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* 배경 및 필터 정의 */}
        <defs>
          {/* 글로우 효과 */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          {/* 마커 글로우 */}
          <filter id="markerGlow">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* 전시장 배경 */}
        <rect 
          x="5" y="5" 
          width="290" height="170" 
          rx="8" 
          fill="#0d1117"
          stroke="#3b82f6"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        
        {/* 전시장 라벨 — 멀티테넌트 venue name 사용 */}
        <text x="150" y="15" textAnchor="middle" fill="#64748b" fontSize="9">
          {getVenueName()}
        </text>
        
        {/* 🔥 실제 부스 레이아웃 표시 + 입출구/편의시설 동적 렌더링 */}
        {boothData.booths.map((booth) => {
          const x = booth.x * SCALE_X;
          const y = booth.y * SCALE_Y;
          const w = Math.max(booth.w * SCALE_X, 2);
          const h = Math.max(booth.h * SCALE_Y, 2);
          
          // 1. 입구 및 출구 (ct: "entrance")
          if (booth.ct === 'entrance') {
            const isExit = booth.i.includes('출구');
            return (
              <g key={booth.i}>
                <rect x={x} y={y} width={w} height={h} rx="2" 
                      fill={isExit ? "#ef4444" : "#3b82f6"} fillOpacity="0.6" />
                <text x={x + w/2} y={y + h/2 + 2} textAnchor="middle" fill="#fff" fontSize="5" fontWeight="bold">
                  {isExit ? '출구' : '입구'}
                </text>
              </g>
            );
          }

          // 2. 편의시설 (ct: "amenity" - 화장실, 비상구)
          if (booth.ct === 'amenity') {
            const isWC = booth.i.startsWith('W.C');
            const isEmergency = booth.i.startsWith('비상구');
            
            if (!isWC && !isEmergency) return null;
            return (
              <g key={booth.i}>
                <rect x={x} y={y} width={w} height={h} rx="1" fill="#475569" fillOpacity="0.8" stroke="#94a3b8" strokeWidth="0.2" />
                <text x={x + w/2} y={y + h/2 + 1.5} textAnchor="middle" fill="#f8fafc" fontSize="4" fontWeight="bold">
                  {isWC ? 'W.C' : 'EXIT'}
                </text>
              </g>
            );
          }
          
          // 3. 일반 부스
          const isFrom = booth.i === fromBooth?.toUpperCase().replace('-', '');
          const isTo = booth.i === toBooth?.toUpperCase().replace('-', '');
          
          // 공통 스타일 속성 추출
          const rectProps = {
            fill: isFrom ? "#22c55e" : isTo ? "#ef4444" : "#1e3a5f",
            fillOpacity: isFrom || isTo ? 0.8 : 0.4,
            stroke: isFrom ? "#22c55e" : isTo ? "#ef4444" : "#3b82f6",
            strokeWidth: isFrom || isTo ? 1.5 : 0.3,
            strokeOpacity: isFrom || isTo ? 1 : 0.5,
            rx: 1
          };

          // 3-1. 복합 부스 처리 (rs 배열이 있는 경우 - L자형, T자형 등)
          if (booth.rs && Array.isArray(booth.rs) && booth.rs.length > 0) {
            return (
              <g key={`group-${booth.i}`}>
                {booth.rs.map((rect, rIdx) => (
                  <rect
                    key={`${booth.i}-rect-${rIdx}`}
                    x={rect.x * SCALE_X}
                    y={rect.y * SCALE_Y}
                    width={Math.max(rect.w * SCALE_X, 2)}
                    height={Math.max(rect.h * SCALE_Y, 2)}
                    {...rectProps}
                  />
                ))}
              </g>
            );
          }

          // 3-2. 단일 부스 처리 (기존 로직)
          return (
            <rect
              key={booth.i}
              x={x}
              y={y}
              width={w}
              height={h}
              {...rectProps}
            />
          );
        })}
        
        {/* 주요 구역 라벨 */}
        <text x="30" y="140" fill="#475569" fontSize="7" opacity="0.7">A</text>
        <text x="30" y="100" fill="#475569" fontSize="7" opacity="0.7">B-F</text>
        <text x="150" y="100" fill="#475569" fontSize="7" opacity="0.7">G-H</text>
        <text x="250" y="100" fill="#475569" fontSize="7" opacity="0.7">I-L</text>
        <text x="280" y="80" fill="#475569" fontSize="7" opacity="0.7">M</text>
        
        {/* 열 번호 그리드 */}
        {[1, 10, 20, 30, 40].map((col) => (
          <g key={col}>
            <line 
              x1={20 + (col / 40) * 260} 
              y1="30" 
              x2={20 + (col / 40) * 260} 
              y2="160" 
              stroke="#334155" 
              strokeWidth="0.5"
              strokeDasharray="2,4"
            />
            <text 
              x={20 + (col / 40) * 260} 
              y="170" 
              fill="#475569" 
              fontSize="8"
              textAnchor="middle"
            >
              {col.toString().padStart(2, '0')}
            </text>
          </g>
        ))}
        
        {/* 🆕 모든 편의시설 아이콘 표시 (화장실/카페 등) */}
        {facilities.map((f) => (
          <g key={`fac-${f.i}`}>
            <circle 
              cx={f.x * SCALE_X} cy={f.y * SCALE_Y} r="5" 
              fill="#3b82f6" filter="url(#markerGlow)" 
            />
            <text 
              x={f.x * SCALE_X} y={f.y * SCALE_Y + 1.5} 
              fontSize="4" fill="white" textAnchor="middle" fontWeight="bold"
            >
              {f.i.includes('W.C') ? 'WC' : '☕'}
            </text>
          </g>
        ))}
        
        {/* 🔥 경로 선 - 복도 기반 실제 경로 */}
        {pathD && (
          <>
            {/* 경로 그림자 (두꺼운 배경) */}
            <path 
              d={pathD} 
              fill="none" 
              stroke="#3b82f6"
              strokeWidth="6"
              strokeOpacity="0.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* 경로 메인 라인 */}
            <path 
              d={pathD} 
              fill="none" 
              stroke="#ff3875"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="8,4"
              filter="url(#glow)"
            >
              {/* 점선 애니메이션 */}
              <animate 
                attributeName="stroke-dashoffset" 
                from="24" 
                to="0" 
                dur="1s" 
                repeatCount="indefinite"
              />
            </path>
            
            {/* 🔥 경로 중간 웨이포인트 표시 (선택적) */}
            {/* {scaledPathPoints.length > 2 && scaledPathPoints.slice(1, -1).map((point, idx) => (
              <circle
                key={`waypoint-${idx}`}
                cx={point.x}
                cy={point.y}
                r="3"
                fill="#3b82f6"
                fillOpacity="0.6"
                stroke="#fff"
                strokeWidth="1"
              />
            ))} */}
          </>
        )}
        
        {/* 출발점 마커 */}
        {fromPos && (
          <g className="marker-from">
            {/* 펄스 애니메이션 */}
            <circle 
              cx={fromPos.x} 
              cy={fromPos.y} 
              r="12"
              fill="#22c55e"
              fillOpacity="0.2"
            >
              <animate 
                attributeName="r" 
                values="8;14;8" 
                dur="1.5s" 
                repeatCount="indefinite"
              />
              <animate 
                attributeName="fill-opacity" 
                values="0.4;0.1;0.4" 
                dur="1.5s" 
                repeatCount="indefinite"
              />
            </circle>
            {/* 마커 */}
            <circle 
              cx={fromPos.x} 
              cy={fromPos.y} 
              r="6"
              fill="#22c55e"
              stroke="#fff"
              strokeWidth="2"
              filter="url(#markerGlow)"
            />
            {/* 라벨 */}
            <text 
              x={fromPos.x} 
              y={fromPos.y - 12} 
              textAnchor="middle" 
              fill="#22c55e" 
              fontSize="9"
              fontWeight="bold"
            >
              {fromBooth}
            </text>
          </g>
        )}
        
        {/* 도착점 마커 */}
        {toPos && (
          <g className="marker-to">
            {/* 펄스 애니메이션 */}
            <circle 
              cx={toPos.x} 
              cy={toPos.y} 
              r="12"
              fill="#ef4444"
              fillOpacity="0.2"
            >
              <animate 
                attributeName="r" 
                values="8;14;8" 
                dur="1.5s" 
                repeatCount="indefinite"
              />
              <animate 
                attributeName="fill-opacity" 
                values="0.4;0.1;0.4" 
                dur="1.5s" 
                repeatCount="indefinite"
              />
            </circle>
            {/* 마커 */}
            <circle 
              cx={toPos.x} 
              cy={toPos.y} 
              r="6"
              fill="#ef4444"
              stroke="#fff"
              strokeWidth="2"
              filter="url(#markerGlow)"
            />
            {/* 라벨 */}
            <text 
              x={toPos.x} 
              y={toPos.y - 12} 
              textAnchor="middle" 
              fill="#ef4444" 
              fontSize="9"
              fontWeight="bold"
            >
              {toBooth}
            </text>
          </g>
        )}
      </svg>        
      
      {/* 범례 + 경로 정보 */}
      <div className="minimap-legend">
        <div className="legend-item">
          <span className="legend-dot from"></span>
          <span>출발: {fromBooth}</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot to"></span>
          <span>도착: {toBooth}</span>
        </div>
        {/* 🔥 경로 정보 표시 */}
        {/* {pathInfo && pathInfo.waypoints > 2 && (
          <div className="legend-item path-info">
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>
              약 {pathInfo.meters}m · {pathInfo.minutes}분
            </span>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default ChatbotMiniMap;
